import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Bell, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import { reminderItems, type ReminderItem } from '@/lib/reminders';
import { useIncrementalRender } from '@/lib/useIncrementalRender';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';

function dueLabel(item: ReminderItem, t: ReturnType<typeof useT>): string {
  if (item.overdue)
    return `${Math.abs(item.daysUntil)} ${t('reminders.daysOverdue')}`;
  if (item.daysUntil === 0) return t('reminders.today');
  return `${item.daysUntil} ${t('reminders.dueInDays')}`;
}

function ReminderRow({ item }: { item: ReminderItem }) {
  const t = useT();
  // The row opens the customer — only a link when the user may read
  // customers (#95).
  const canOpen = useSectionRights()[SectionId.CUSTOMERS].read;
  const rowClass =
    'flex items-center justify-between gap-3 rounded-md border p-3 transition-colors';
  const body = (
    <>
      <div className="min-w-0">
        <div className="font-medium truncate text-primary">
          {item.customerName}
        </div>
        <div className="text-xs text-muted-foreground">
          {fmtDate(item.date)}
        </div>
        {item.siteAddress && (
          <div className="text-xs text-muted-foreground truncate">
            {item.siteAddress}
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-1">
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
            {t('customer.extinguishers')}: {item.extinguisherCount}
          </Badge>
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
            {t('customer.hoses')}: {item.hoseCount}
          </Badge>
        </div>
      </div>
      <span
        className={
          item.overdue
            ? 'text-xs font-semibold text-destructive shrink-0'
            : 'text-xs font-medium text-amber-600 dark:text-amber-400 shrink-0'
        }
      >
        {dueLabel(item, t)}
      </span>
    </>
  );
  return canOpen ? (
    <Link
      to={`/customer/${item.customerId}`}
      className={`${rowClass} hover:bg-accent/50`}
    >
      {body}
    </Link>
  ) : (
    <div className={rowClass}>{body}</div>
  );
}

export function Reminders() {
  const t = useT();
  const customers = useApp(s => s.customers);
  const interval = useApp(s => s.settings.inspectionInterval);
  const items = useMemo(
    () => reminderItems(customers, 30, undefined, interval),
    [customers, interval]
  );
  const overdue = useMemo(() => items.filter(i => i.overdue), [items]);
  const soon = useMemo(() => items.filter(i => !i.overdue), [items]);
  const {
    visible: visibleOverdue,
    sentinelRef: overdueSentinelRef,
    hasMore: overdueHasMore,
  } = useIncrementalRender(overdue);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Bell className="h-6 w-6 text-primary" /> {t('reminders.title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('reminders.subtitle')}
        </p>
      </div>

      {items.length === 0 && (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            {t('reminders.none')}
          </CardContent>
        </Card>
      )}

      {overdue.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" /> {t('reminders.overdue')} (
              {overdue.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {visibleOverdue.map(i => (
              <ReminderRow key={i.customerId} item={i} />
            ))}
            {overdueHasMore && (
              <div ref={overdueSentinelRef} aria-hidden className="h-px" />
            )}
          </CardContent>
        </Card>
      )}

      {soon.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <Clock className="h-4 w-4" /> {t('reminders.dueSoon')} (
              {soon.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {soon.map(i => (
              <ReminderRow key={i.customerId} item={i} />
            ))}
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
